import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { Sheet } from '../../../mobile/structural/sheet/Sheet';
import useSheet from '../../../mobile/structural/sheet/hooks/useSheet';
import SheetSearchBar from '../../../mobile/structural/sheet/searchBar/SheetSearchBar';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { focusFakeInput, holdFocus } from '../../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { useTranslator } from '../../../components/i18n/useTranslator';
import FormulaDropdownListItem from './FormulaDropdownListItem';
import useFormulaPickerController from './useFormulaPickerController';
import type { FormulaPickerRef } from './useFormulaPickerController';

import './TableFormulaSheet.scss';

type TableFormulaSheetProps = {
    addFormulaToCell: (formula: string, replacePrevCharacters: number) => void;
    formulaPickerRef: MutableRefObject<FormulaPickerRef | null>;
    refocusCellEditor: () => void;
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
};

const TableFormulaSheet: React.FC<TableFormulaSheetProps> = ({
    addFormulaToCell,
    formulaPickerRef,
    refocusCellEditor,
    show,
    setShow,
}) => {
    const translateTableToolbar = useTranslator('toolbar', 'tools.editing.table');

    const [isScrolled, setIsScrolled] = useState(false);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const { focusedIndex, formulaPickerData, formulaQuery, hideFormulaPicker, selectFormula, setFormulaPickerQuery } =
        useFormulaPickerController({ show, setShow, addFormulaToCell, replaceFormulaQueryOnInsert: false });

    const onCloseTransitionEnd = useCallback(() => {
        hideFormulaPicker();
        refocusCellEditor();
    }, [hideFormulaPicker, refocusCellEditor]);

    // `onCloseTransitionEnd` has to be a hook option rather than a `<Sheet>` prop, or it replaces the
    // adapter's redux teardown (WEB-16763) — which is why the picker controller lives here alongside it
    const sheetProps = useSheet(SheetId.TableFormulaTool, undefined, { onCloseTransitionEnd });
    const { closeSheet, openSheet, isOpen, contentRef } = sheetProps;

    useEffect(() => {
        if (isOpen === show) return;

        if (show) {
            openSheet();
            return;
        }

        holdFocus();
        closeSheet();
    }, [closeSheet, openSheet, isOpen, show]);

    useImperativeHandle(formulaPickerRef, () => ({ setFormulaPickerQuery, hideFormulaPicker }), [
        hideFormulaPicker,
        setFormulaPickerQuery,
    ]);

    const onOpenTransitionEnd = useCallback(() => {
        if (contentRef.current) focusFakeInput({ x: 0, y: 0 }, contentRef.current);
        searchInputRef.current?.focus();
    }, [contentRef]);

    return (
        <Sheet
            className="TableFormulaSheet"
            {...sheetProps}
            defaultSnapPoint={1}
            onOpenTransitionEnd={onOpenTransitionEnd}
            // Closing this sheet returns focus to the cell editor, so its keyboard must remain visible.
            preserveKeyboardOnDrag
        >
            <Sheet.Panel>
                <div data-table-util>
                    <Sheet.Header>
                        <Sheet.Heading>{translateTableToolbar('formula')}</Sheet.Heading>
                        <Sheet.DoneButton onClick={hideFormulaPicker} />
                    </Sheet.Header>
                    <SheetSearchBar
                        searchFilter={formulaQuery}
                        setSearchFilter={setFormulaPickerQuery}
                        enterKeyHint="search"
                        inputRef={searchInputRef}
                        showFade={isScrolled}
                    />
                    <Sheet.Content
                        className="formula-list-content"
                        onScroll={(event) => setIsScrolled((event.target as HTMLDivElement).scrollTop > 0)}
                    >
                        <ul className="formula-list">
                            {formulaPickerData.map((formula, index) => (
                                <FormulaDropdownListItem
                                    formulaQuery={formulaQuery}
                                    focusedIndex={focusedIndex}
                                    key={formula}
                                    formula={formula}
                                    index={index}
                                    onSelectFormula={selectFormula}
                                />
                            ))}
                        </ul>
                    </Sheet.Content>
                </div>
            </Sheet.Panel>
        </Sheet>
    );
};

export default TableFormulaSheet;
